/**
 * Profile Update Request Modal (Feature 022)
 *
 * Modal that allows service providers to submit profile update requests
 * to the bQuest support team.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Textarea } from '@/shared/components/ui/textarea';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { apiClient } from '@/core/api/client';

type ProfileUpdateRequestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  providerName: string;
  providerId: string;
  providerEmail: string;
};

export function ProfileUpdateRequestModal({
  isOpen,
  onClose,
  providerName,
  providerId,
  providerEmail,
}: ProfileUpdateRequestModalProps) {
  const [updateRequest, setUpdateRequest] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async () => {
    if (!updateRequest.trim()) {
      setErrorMessage('Please describe the updates you would like to make.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      await apiClient.post('/providers/profile-update-request', {
        providerId,
        providerName,
        providerEmail,
        updateRequest: updateRequest.trim(),
      });

      setSubmitStatus('success');
      setUpdateRequest('');

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSubmitStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Error submitting profile update request:', error);
      setSubmitStatus('error');
      setErrorMessage('Failed to submit your request. Please try again or contact support directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setUpdateRequest('');
      setSubmitStatus('idle');
      setErrorMessage('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Request Profile Updates</DialogTitle>
          <DialogDescription>
            Let us know what updates you'd like to make and our team will take a look.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {submitStatus === 'success' ? (
            <Alert className="bg-emerald-50 border-emerald-200">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <AlertDescription className="text-emerald-800">
                Your request has been submitted successfully! Our team will review it and get back to you soon.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <Textarea
                placeholder="Please describe the updates you would like to make to your profile..."
                value={updateRequest}
                onChange={(e) => setUpdateRequest(e.target.value)}
                rows={6}
                className="resize-none"
                disabled={isSubmitting}
              />

              {errorMessage && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}

              {submitStatus === 'error' && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Failed to submit your request. Please try again or contact{' '}
                    <a
                      href="mailto:info@thebquest.com"
                      className="underline font-medium"
                    >
                      info@thebquest.com
                    </a>{' '}
                    directly.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          {submitStatus !== 'success' && (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !updateRequest.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
